import React, { useState, useEffect, useRef } from "react";
import { IoCloseSharp } from "react-icons/io5";

interface LogModalProps {
  items: string[];
  isOpen: boolean;
  onClose: (state: boolean) => void;
  handleKeyword: (keyword: string) => void;
  inputRef: useRef<HTMLInputElement>;
  removeSearchTeamHistory: (item: string) => void;
}

const SearchHistoryModal: React.FC<LogModalProps> = ({
  items,
  isOpen,
  onClose,
  handleKeyword,
  inputRef,
  removeSearchTeamHistory,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [positions, setPositions] = useState<{ top: number; left: number }[]>([]);

  useEffect(() => {
    // 모달이 열릴 때 요소 위치 저장

    const updatePositions = () => {
      if (!containerRef.current) return;
      const children = Array.from(containerRef.current.children) as HTMLDivElement[];
      const newPositions = children.map((child) => {
        const rect = child.getBoundingClientRect();
        return { top: rect.top, left: rect.left };
      });
      setPositions(newPositions);
    };

    updatePositions();
    window.addEventListener("resize", updatePositions);

    return () => window.removeEventListener("resize", updatePositions);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSelectedIndex(-1);
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      let newIndex = selectedIndex;

      if (e.key === "ArrowRight") {
        if (inputRef.current) {
          inputRef.current.blur();
        }
        if (selectedIndex === -1) {
          newIndex = 0;
        } else {
          newIndex = Math.min(selectedIndex + 1, items.length - 1);
        }
      } else if (e.key === "ArrowLeft") {
        if (inputRef.current) {
          inputRef.current.blur();
        }
        if (selectedIndex === -1) {
          newIndex = 0;
        } else {
          newIndex = Math.max(selectedIndex - 1, 0);
        }
      } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (positions.length === 0) return;
        if (inputRef.current) {
          inputRef.current.blur();
        }

        const current = positions[selectedIndex];
        let closestIndex = selectedIndex;
        let minDistance = Infinity;

        positions.forEach((pos, index) => {
          if (index !== selectedIndex) {
            const isBelow = e.key === "ArrowDown" && pos.top > current.top;
            const isAbove = e.key === "ArrowUp" && pos.top < current.top;

            if (isBelow || isAbove) {
              const distance = Math.abs(pos.top - current.top) + Math.abs(pos.left - current.left);
              if (distance < minDistance) {
                minDistance = distance;
                closestIndex = index;
              }
            }
          }
        });

        if (selectedIndex === -1) {
          newIndex = 0;
        } else {
          newIndex = closestIndex;
        }
      } else if (e.key === "Escape") {
        if (inputRef.current) {
          inputRef.current.focus();
        }
        onClose(false); // ESC 키로 모달 닫기
        return;
      } else if (e.key === "Enter") {
        if (inputRef.current) {
          inputRef.current.focus();
        }
        onClose(false);
        if (selectedIndex === -1) {
          return;
        }
        handleKeyword(items[selectedIndex]);
      }

      setSelectedIndex(newIndex);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, positions, onClose, isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="absolute z-30 flex flex-wrap w-full p-2 mt-1 space-x-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 top-full"
      ref={containerRef}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className={`p-2 m-1 flex border border-gray-300  rounded-full whitespace-nowrap cursor-default place-content-center hover:bg-blue-500 hover:text-white ${
            selectedIndex === index ? "bg-blue-500 text-white" : "bg-gray-200"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleKeyword(item);
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          {item}
          <div
            className="ml-1 cursor-pointer place-content-center hover:text-red-500"
            onClick={(e) => {
              e.stopPropagation();
              removeSearchTeamHistory(item);
            }}
          >
            <IoCloseSharp />
          </div>
        </div>
      ))}
    </div>
  );
};

export default SearchHistoryModal;
